"use client";

import { mediaSyncDiagnostics } from "@/lib/live/media-sync/media-sync";
import { currentLiveSession } from "@/lib/live/live-debug";
import { isCameraPagePath } from "@/lib/live/camera-link";
import { usePathname, useRouter } from "next/navigation";
import { standard88Keys } from "@/lib/piano/piano";
import { studioViewFromPathname, studioViewPath } from "@/lib/studio/studio-routes";
import { track, trackOnce } from "@/lib/analytics";
import {
  recordMetronomeUsed,
  recordTempoChange,
} from "@/lib/analytics/studio-session";
import {
  Studio,
  type DivisionId,
  type NoteEffectStyle,
  type Signature,
  type StudioView,
} from "@/lib/studio/studio";
import type { Engagement } from "@/lib/studio/engagement";
import { DEFAULT_NOTE_EFFECT_STYLE } from "@/lib/engine/components/piano-roll/note-effects/types";
import { DEFAULT_KEYBOARD_HEIGHT } from "@/lib/piano/horizontal-projection";
import { MINIMAP_WIDTH } from "@/lib/studio/view/components/minimap-component";
import { PLAYHEAD_DISTANCE_PX } from "@/lib/studio/view/playhead-distance";
import type { Tonality } from "@/lib/music/tonality";
import type { ExerciseDetail } from "@/lib/api/api-client";
import type { MidiDeviceInfo } from "@/lib/piano/input/midi";
import { MicrophoneController } from "@/lib/piano/input/controllers/microphone-controller";
import {
  describeMicError,
  describeMicUnsupported,
  probeMicCapture,
  type MicUnsupportedReason,
} from "@/lib/piano/input/mic-support";
import { MidiController } from "@/lib/piano/input/controllers/midi-controller";
import { KeyboardController } from "@/lib/piano/input/controllers/keyboard-controller";
import { unlockAudioOnGesture } from "@/lib/audio/unlock-on-gesture";
import { getToneLookAhead, releaseToneContext } from "@/lib/audio/tone-context";
import { observeAudioState } from "@/lib/analytics/trail";
import {
  clearNoteTrace,
  noteTraceCsv,
  noteTraceEntries,
  noteTracePairs,
  noteTraceSummary,
  setNoteTraceEnabled,
} from "@/lib/live/note-trace";
import { getFpsWatchdog } from "@/lib/perf/fps-watchdog";
import {
  DEFAULT_KEEPALIVE_MS,
  radioKeepaliveMs,
  setRadioKeepaliveMs,
} from "@/lib/live/radio-keepalive";
import { getPerfProfileStore } from "@/lib/perf/detect-perf-tier";
import { PerfCoordinator } from "@/lib/perf/perf-coordinator";
import { useObservable } from "@/hooks/use-observable";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { ReadonlyObservable } from "@/lib/studio/observable";

type LoadExerciseFn = (exercise: ExerciseDetail) => Promise<void>;

/**
 * Loads a public piece from the catalog into the studio. Resolves with
 * the persisted `midiFileId` (when the source is MIDI) so the caller can
 * route to `/learn-piece/{midiFileId}` after a successful load.
 */
/**
 * `scoreId` quand on sait quel fichier de l'œuvre ouvrir (une ligne de « Mes
 * morceaux ») ; sans lui, le serveur choisit — voir `loadPieceSong`.
 */
type LoadPieceFn = (
  pieceId: string,
  scoreId?: string,
) => Promise<{ midiFileId: string | null }>;

interface StudioContextValue {
  studio: Studio | null;
  micController: MicrophoneController | null;
  midiController: MidiController | null;
  keyboardController: KeyboardController | null;
  /** Load an exercise into the piano roll. Registered by StudioPage. */
  loadExercise: LoadExerciseFn;
  /** Register the loadExercise implementation (called by StudioPage). */
  registerLoadExercise: (fn: LoadExerciseFn) => void;
  /** Load a catalog piece into the piano roll. Registered by StudioPage. */
  loadPiece: LoadPieceFn;
  /** Register the loadPiece implementation (called by StudioPage). */
  registerLoadPiece: (fn: LoadPieceFn) => void;
  /** Whether the studio settings overlay is currently visible. Lifted here
   *  so the AppHeader (mounted above StudioPage) can toggle it. */
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
}

const StudioContext = createContext<StudioContextValue | null>(null);

/**
 * Remonte la latence de sortie audio, une fois par contexte.
 *
 * `baseLatency` est disponible dès la création, mais `outputLatency` ne se
 * renseigne qu'une fois le contexte réellement démarré — d'où l'attente de
 * l'état `running` (le navigateur suspend le contexte tant qu'aucun geste
 * utilisateur n'a eu lieu). C'est la mesure qui dit si le retour au clavier
 * est jouable sur le matériel des bêta-testeurs : au-delà de ~40 ms, jouer
 * en rythme devient désagréable, et personne ne le signalera en ces termes.
 *
 * Rend de quoi retirer l'écouteur : si le contexte ne passe jamais à
 * `running` avant le démontage, il resterait accroché à un contexte fermé.
 */
function reportAudioLatency(context: AudioContext): () => void {
  const emit = () => {
    track({
      name: "audio_latency_measured",
      properties: {
        ms: Math.round(context.baseLatency * 1000),
        outputLatencyMs:
          typeof context.outputLatency === "number"
            ? Math.round(context.outputLatency * 1000)
            : undefined,
        // La fenêtre d'anticipation de Tone s'ajoute aux deux précédentes sur
        // toute note déclenchée sans horodatage. Sans elle dans le relevé, la
        // mesure sous-estime de beaucoup ce que le joueur ressent.
        lookAheadMs: Math.round(getToneLookAhead() * 1000),
      },
    });
  };

  if (context.state === "running") {
    emit();

    return () => {};
  }

  const onStateChange = () => {
    if (context.state !== "running") {
      return;
    }

    context.removeEventListener("statechange", onStateChange);
    emit();
  };

  context.addEventListener("statechange", onStateChange);

  return () => {
    context.removeEventListener("statechange", onStateChange);
  };
}

/**
 * Sonde audio de développement, exposée sur `window.__virtuosa`.
 *
 * Un son qui se tait sur iPad ne laisse aucune trace : pas d'exception, pas
 * de promesse rejetée, juste un `AudioContext` dont `currentTime` cesse
 * d'avancer. Il n'y avait jusqu'ici aucun moyen d'observer ça depuis la
 * console de l'inspecteur, faute de poignée sur le contexte de la session.
 *
 * Complément interactif de ce que la mesure fait déjà toute seule :
 * `observeAudioState` (`lib/analytics/trail.ts`) consigne les transitions du
 * contexte pour les rapports de panne, et le `RangeRunner` règle une course
 * figée. Ni l'un ni l'autre ne répond à la question qu'on se pose la tablette
 * en main : est-ce que l'horloge avance, et est-ce qu'un son sort.
 *
 * Rien de tout ceci n'existe en production.
 */
function installAudioDebugHandle(context: AudioContext, studio: Studio): () => void {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
    return () => {};
  }

  let watchId: ReturnType<typeof setInterval> | null = null;

  const onStateChange = () => {
    console.info(
      `[audio] statechange -> ${context.state} @ ${context.currentTime.toFixed(3)}s`,
    );
  };

  context.addEventListener("statechange", onStateChange);

  const handle = {
    /** Le contexte de la session, pour tout inspecter à la main. */
    ctx: context,
    studio,

    /** Photo de l'état audio. Le premier réflexe. */
    state() {
      const snapshot = {
        state: context.state,
        currentTime: context.currentTime,
        sampleRate: context.sampleRate,
        baseLatency: context.baseLatency,
        outputLatency: (context as AudioContext & { outputLatency?: number })
          .outputLatency,
        maxChannelCount: context.destination.maxChannelCount,
        channelCount: context.destination.channelCount,
      };
      console.table(snapshot);

      return snapshot;
    },

    /**
     * L'horloge avance-t-elle ? C'est la seule question qui compte : tout le
     * séquencement (count-in compris) est cadencé dessus.
     */
    async tick(ms = 1000) {
      const before = context.currentTime;
      await new Promise((r) => setTimeout(r, ms));
      const after = context.currentTime;
      const advanced = after - before;
      console.info(
        `[audio] ${context.state} | currentTime ${before.toFixed(3)} -> ${after.toFixed(3)} (+${advanced.toFixed(3)}s en ${ms}ms)`,
      );

      return { state: context.state, before, after, advanced };
    },

    /** Bip sur le contexte de l'app. Entendu = la sortie audio est vivante. */
    beep(durationMs = 400, freq = 440) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      gain.gain.value = 0.2;
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(context.destination);
      osc.start();
      osc.stop(context.currentTime + durationMs / 1000);
      console.info(`[audio] beep programmé, state=${context.state}`);
    },

    /** Forcer le réveil. À appeler depuis un geste si le refus persiste. */
    async resume() {
      try {
        await context.resume();
      } catch (err) {
        console.warn("[audio] resume refusé:", err);
      }

      console.info(`[audio] state=${context.state} t=${context.currentTime.toFixed(3)}`);

      return context.state;
    },

    /** Journal continu de l'horloge, pour voir le moment exact du gel. */
    watch(intervalMs = 500) {
      handle.unwatch();
      watchId = setInterval(() => {
        console.info(
          `[audio] ${context.state} t=${context.currentTime.toFixed(3)}`,
        );
      }, intervalMs);
    },

    unwatch() {
      if (watchId !== null) {
        clearInterval(watchId);
        watchId = null;
      }
    },

    /**
     * Arme ou coupe le journal de notes, et le garde au rechargement.
     *
     * Le drapeau survit exprès : ce qu'on veut souvent observer se passe **à la
     * jointure** d'une séance, avant qu'on ait eu le temps d'ouvrir une
     * console. On arme, on recharge, on joue.
     */
    trace(on = true) {
      setNoteTraceEnabled(on);
      console.info(`[trace] journal de notes ${on ? "armé" : "coupé"}`);

      return on;
    },

    /** Le résumé — à lire en premier. Une ligne par lane. */
    notes() {
      const summary = noteTraceSummary();
      console.table(summary);

      return summary;
    },

    /** Chaque note reconstituée : durée voulue, durée entendue, écart. */
    notePairs() {
      const pairs = noteTracePairs();
      console.table(pairs);

      return pairs;
    },

    /** Le journal brut, dans l'ordre. */
    noteLog() {
      return noteTraceEntries();
    },

    /**
     * Le journal en CSV.
     *
     * La console d'un iPad se lit depuis un Mac et se copie mal : une chaîne se
     * sélectionne d'un coup, un tableau d'objets non.
     */
    noteCsv() {
      const csv = noteTraceCsv();
      console.log(csv);

      return csv;
    },

    clearNotes() {
      clearNoteTrace();
      console.info("[trace] journal vidé");
    },

    /** Où en est le calage de la vidéo et du son sur les notes, sur ce poste. */
    mediaSync() {
      const diagnostics = mediaSyncDiagnostics();
      console.table(diagnostics);

      return diagnostics;
    },

    /**
     * La séance live en cours, `null` hors séance : ses moteurs vidéo et audio,
     * pour les éprouver à la main là où aucun écran ne les montre encore.
     */
    live() {
      return currentLiveSession();
    },

    /**
     * La santé du fil principal, lue sur les images rendues.
     *
     * Le gestionnaire MIDI et celui des trames reçues attendent tous deux que
     * le fil soit libre, et chaque image du roll le tient le temps de son
     * rendu. Le garde-fou de perf relève déjà la durée de chaque image rendue
     * (`FpsWatchdog`) : la p95 et la part d'images au-delà de 33 ms disent si
     * le fil coûte une image ou trois à une note. À lire pendant une prise,
     * pas au repos : le rendu se gare à l'arrêt, et il n'y a alors rien.
     */
    fps() {
      const stats = getFpsWatchdog().getStats();

      if (stats === null) {
        console.info("[fps] pas assez d'images rendues : jouer d'abord, le rendu se gare au repos");

        return null;
      }

      console.table(stats);

      return stats;
    },

    /**
     * Règle ou coupe le battement qui tient les radios Wi-Fi éveillées.
     *
     * Allumé par défaut depuis que la mesure l'a validé ; `keepalive(0)` le
     * coupe, pour mesurer sans lui. Voir `lib/live/radio-keepalive.ts`. Prend
     * effet à la prochaine séance, et survit au rechargement.
     */
    keepalive(ms: number = DEFAULT_KEEPALIVE_MS) {
      setRadioKeepaliveMs(ms);

      if (ms > 0) {
        console.info(`[live] battement toutes les ${ms} ms, à la prochaine séance`);
      } else {
        console.info("[live] battement coupé, à la prochaine séance");
      }

      return radioKeepaliveMs();
    },
  };

  (window as unknown as { __virtuosa: typeof handle }).__virtuosa = handle;
  console.info("[audio] sonde prête: __virtuosa.state() .tick() .beep() .resume() .watch() .fps()");
  console.info(
    "[trace] notes: __virtuosa.trace() puis .notes() .notePairs() .noteCsv() .clearNotes()",
  );
  console.info("[live] radio: __virtuosa.keepalive(0) pour couper le battement, .keepalive(50) par défaut");
  console.info("[live] séance: __virtuosa.live() .audio .video .timing, synchro: __virtuosa.mediaSync()");

  return () => {
    handle.unwatch();
    context.removeEventListener("statechange", onStateChange);
    delete (window as unknown as { __virtuosa?: unknown }).__virtuosa;
  };
}

interface StudioBundle {
  studio: Studio;
  mic: MicrophoneController;
  midi: MidiController;
  keyboard: KeyboardController;
}

export function StudioProvider({ children }: { children: ReactNode }) {
  const [bundle, setBundle] = useState<StudioBundle | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const loadExerciseRef = useRef<LoadExerciseFn | null>(null);
  const loadPieceRef = useRef<LoadPieceFn | null>(null);

  useEffect(() => {
    // La page du téléphone qui filme un récital n'a pas de studio : ni son, ni
    // rouleau, ni clavier. Y monter le moteur coûterait un contexte audio et un
    // graphe d'objets pendant une heure, sur batterie. Lu une fois au montage :
    // on y arrive par un QR code, jamais en naviguant depuis le studio.
    if (isCameraPagePath(window.location.pathname)) {
      return;
    }

    const perfStore = getPerfProfileStore();

    // Le tampon de sortie ne se demande qu'ici : `latencyHint` est une option
    // de construction, aucune API ne le rejoue sur un contexte vivant. C'est
    // pour ça qu'il ne figure pas dans les leviers de l'auto-dégradation — un
    // changement de palier en cours de séance ne le reprendra pas, il faut
    // recharger. Voir `AudioProfile.outputLatencyHint`.
    const audioContext = new AudioContext({
      latencyHint: perfStore.value.audio.outputLatencyHint,
    });

    const piano = standard88Keys;
    const studio = new Studio(piano, audioContext);
    const mic = new MicrophoneController(piano, audioContext);
    const midi = new MidiController(piano);
    const keyboard = new KeyboardController(piano);

    // The FPS watchdog is driven by the render loop (StudioView feeds it each
    // rendered frame) rather than a standalone rAF, so it measures real frames
    // and goes quiet when the renderer parks at idle. No explicit boot needed.

    // Auto-downgrade coordinator. Watches FPS during transport playback
    // and trims effects + sampler strategy if the device can't keep up.
    // Disabled when the user has manually forced a tier (cf. settings).
    const coordinator = new PerfCoordinator(
      getPerfProfileStore(),
      getFpsWatchdog(),
      { isPlaying: () => !studio.getLocalTransport().isDetached },
      {
        onDowngrade: () => console.info("[perf] auto-downgrade applied"),
        // La santé de rendu ne se mesure que sur du vrai matériel : c'est le
        // seul moyen de savoir si le piano-roll rame chez les bêta-testeurs,
        // qui ne le signaleront pas d'eux-mêmes.
        onSample: (stats) =>
          track({
            name: "render_fps_sampled",
            properties: {
              fps: stats.fps,
              medianMs: Math.round(stats.median * 100) / 100,
              p95Ms: Math.round(stats.p95 * 100) / 100,
              jankRatio: Math.round(stats.jankRatio * 1000) / 1000,
              perfTier: getPerfProfileStore().value.tier,
            },
          }),
      },
    );
    coordinator.start();

    const releaseAudioLatency = reportAudioLatency(audioContext);
    const releaseAudioUnlock = unlockAudioOnGesture(audioContext, window);
    // Pour les rapports de panne : `interrupted` posé par Safari à la mise en
    // veille est la ligne qu'on cherche quand un décompte ne part pas.
    const releaseAudioTrail = observeAudioState(audioContext);
    const releaseAudioDebug = installAudioDebugHandle(audioContext, studio);

    setBundle({ studio, mic, midi, keyboard });

    return () => {
      releaseAudioLatency();
      releaseAudioUnlock();
      releaseAudioTrail();
      releaseAudioDebug();
      // Controllers first (they release input listeners and the mic stream),
      // then the studio (which tears down view + transport), then the
      // AudioContext last because both studio and mic write into it.
      coordinator.stop();
      mic.dispose();
      midi.dispose();
      keyboard.dispose();
      // `releaseToneContext` ferme l'`AudioContext` lui-même : le wrapper Tone
      // possède un ticker Web Worker qui survivrait à un simple `close()`.
      void studio.destroy().then(() => {
        void releaseToneContext(audioContext);
      });
    };
  }, []);

  const loadExercise = useCallback<LoadExerciseFn>(async (exercise) => {
    // Lève, comme `loadPiece` juste en dessous, au lieu de ne rien faire.
    // Le silence renvoyait l'appelant à un studio vide en lui laissant croire
    // que le chargement avait eu lieu : la pré-séance naviguait vers
    // `/studio`, aucun exercice n'y était, et rien ne le disait — ni à
    // l'écran, ni dans la mesure.
    if (!loadExerciseRef.current) {
      throw new Error(
        "loadExercise called before StudioPage registered an implementation",
      );
    }

    await loadExerciseRef.current(exercise);
  }, []);

  const registerLoadExercise = useCallback((fn: LoadExerciseFn) => {
    loadExerciseRef.current = fn;
  }, []);

  const loadPiece = useCallback<LoadPieceFn>(async (pieceId, scoreId) => {
    if (!loadPieceRef.current) {
      throw new Error(
        "loadPiece called before StudioPage registered an implementation",
      );
    }

    return loadPieceRef.current(pieceId, scoreId);
  }, []);

  const registerLoadPiece = useCallback((fn: LoadPieceFn) => {
    loadPieceRef.current = fn;
  }, []);

  const value = useMemo(
    () => ({
      studio: bundle?.studio ?? null,
      micController: bundle?.mic ?? null,
      midiController: bundle?.midi ?? null,
      keyboardController: bundle?.keyboard ?? null,
      loadExercise,
      registerLoadExercise,
      loadPiece,
      registerLoadPiece,
      settingsOpen,
      setSettingsOpen,
    }),
    [
      bundle,
      loadExercise,
      registerLoadExercise,
      loadPiece,
      registerLoadPiece,
      settingsOpen,
    ],
  );

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  );
}

export function useStudioSettingsOpen(): [boolean, (open: boolean) => void] {
  const { settingsOpen, setSettingsOpen } = useStudio();

  return [settingsOpen, setSettingsOpen];
}

export function useStudio() {
  const context = useContext(StudioContext);
  if (!context) {
    throw new Error(
      "useStudio doit être utilisé à l'intérieur d'un StudioProvider",
    );
  }

  return context;
}

/**
 * Subscribe to a studio observable, with a fallback used while `studio`
 * is still being initialised (the provider creates it inside an effect).
 * The fallback is wrapped in a stable sentinel observable so the order
 * and identity of subscriptions stay consistent across renders.
 */
function useStudioObservable<T>(
  obs: ReadonlyObservable<T> | null | undefined,
  fallback: T,
): T {
  const fallbackRef = useRef(fallback);
  // The fallback is only consumed when no real observable is available yet,
  // capturing it on first render is enough.
  const sentinel = useMemo<ReadonlyObservable<T>>(
    () => ({
      get value() {
        return fallbackRef.current;
      },
      subscribe: () => () => {},
    }),
    [],
  );

  return useObservable(obs ?? sentinel);
}

/**
 * Ce à quoi le joueur est engagé sur le morceau, et de quoi en changer.
 *
 * `null` est le jeu libre. Un seul axe pour les deux parcours : voir
 * `Engagement`, qui dit pourquoi ce n'est ni un mode ni une vue.
 */
export function useStudioEngagement(): [
  Engagement | null,
  (next: Engagement | null) => void,
] {
  const { studio } = useStudio();
  const engagement = useStudioObservable<Engagement | null>(studio?.engagement, null);

  const setEngagement = useCallback(
    (next: Engagement | null) => {
      studio?.setEngagement(next);
    },
    [studio],
  );

  return [engagement, setEngagement];
}

/**
 * Dernière vue du studio rapportée, au niveau du module.
 *
 * `useStudioView` a plusieurs consommateurs montés en même temps — l'en-tête,
 * la barre d'outils, la page — et ils voient tous la même route au même
 * rendu : sans cette mémoire partagée, un seul changement d'onglet produirait
 * autant d'événements qu'il y a d'abonnés.
 */
let lastReportedStudioView: StudioView | null = null;

/**
 * Active studio view + a setter that *navigates*. Each view has its own
 * route (`/studio`, `/studio/harmony`, …) so tabs are shareable and the back
 * button works, which makes the pathname the source of truth: the setter
 * pushes the route and `StudioPage` mirrors it back into the observable.
 * Writing `studio.setView` directly would desync the URL.
 */
export function useStudioView(): [StudioView, (view: StudioView) => void] {
  const { studio } = useStudio();
  const router = useRouter();
  const pathname = usePathname();
  const view = useStudioObservable<StudioView>(studio?.view, "roll");
  const routeView = studioViewFromPathname(pathname);

  // Le changement d'onglet se constate sur la route, pas sur le bouton.
  //
  // Il était émis dans `setView`, donc uniquement quand quelqu'un cliquait :
  // un retour arrière navigateur changeait bel et bien la vue sans rien
  // laisser, et l'onglet où l'on revient est exactement celui qu'on veut lire.
  // La route étant la source de vérité (voir `studio-routes`), l'observer
  // couvre les deux chemins d'un coup, et le bouton n'y passe qu'une fois :
  // il pousse la route, et c'est la route qui rapporte.
  useEffect(() => {
    if (routeView === null) {
      // Quitter le studio n'est pas changer d'onglet, et y revenir non plus :
      // sans cet oubli, un aller-retour par la bibliothèque rapporterait un
      // faux passage de « harmony » à « roll ».
      lastReportedStudioView = null;

      return;
    }

    if (lastReportedStudioView === routeView) {
      return;
    }

    const from = lastReportedStudioView;
    lastReportedStudioView = routeView;

    // Première vue de la session : c'est une arrivée, que `page_viewed`
    // décrit déjà. Un changement a besoin d'un avant.
    if (from === null) {
      return;
    }

    track({ name: "studio_view_changed", properties: { view: routeView, from } });
  }, [routeView]);

  const setView = useCallback(
    (next: StudioView) => {
      const path = studioViewPath(next);

      // Re-selecting the active view must not stack a history entry, the
      // back button would look broken.
      if (path === pathname) {
        return;
      }

      router.push(path);
    },
    [pathname, router],
  );

  return [view, setView];
}

/**
 * Live keyboard strip height (px), published by the Pixi view on every layout.
 * Overlays that clamp above the keyboard read this so they end exactly at the
 * top of the keys (the strip height now scales with the clamped key width).
 * Falls back to the height at MAX key width before the first layout.
 */
export function usePianoHeight(): number {
  const { studio } = useStudio();

  return useStudioObservable<number>(studio?.keyboardHeight, DEFAULT_KEYBOARD_HEIGHT);
}

/**
 * Largeur courante du rail de minimap (px), publiée par la vue Pixi à chaque
 * layout. Les overlays qui s'arrêtent au bord du rail la lisent ici : elle
 * dépend de la largeur du canvas, la recopier en dur les ferait diverger du
 * rendu Pixi au premier redimensionnement. Repli sur la largeur des écrans
 * larges avant le premier layout.
 */
export function useMinimapWidth(): number {
  const { studio } = useStudio();

  return useStudioObservable<number>(studio?.minimapWidth, MINIMAP_WIDTH);
}

/**
 * Distance courante (px) entre la playhead et le bas du roll, publiée par la
 * vue Pixi à chaque layout. Elle redescend vers le piano quand le roll manque
 * de hauteur : les overlays qui réservent un couloir de lecture au-dessus de
 * la playhead doivent la lire ici, une constante recopiée réserverait un
 * couloir au-dessus d'une ligne qui n'y est plus. Repli sur la distance
 * nominale, le cas des écrans hauts, avant le premier layout.
 */
export function usePlayheadDistance(): number {
  const { studio } = useStudio();

  return useStudioObservable<number>(studio?.playheadDistance, PLAYHEAD_DISTANCE_PX);
}

export function useBpm(): [number, (bpm: number) => void] {
  const { studio } = useStudio();
  const bpm = useStudioObservable<number>(studio?.bpm, 120);

  const setBpm = useCallback(
    (next: number) => {
      // Le tempo se compte sur le rappel et non sur l'observable : chaque
      // morceau chargé republie une valeur par `setOriginalBpm`, et compter
      // cette republication ferait de `tempoChanges` un compteur
      // d'ouvertures. Ce rappel-ci n'a qu'un appelant, la barre d'outils,
      // donc une intention de ralentir et rien d'autre. Les rafales du
      // curseur sont regroupées par l'agrégat, pas ici.
      if (studio && studio.bpm.value !== next) {
        recordTempoChange();
      }

      studio?.setBpm(next);
    },
    [studio],
  );

  return [bpm, setBpm];
}

export function useOriginalBpm(): number {
  const { studio } = useStudio();
  const [bpm, setBpmState] = useState<number>(() => studio?.originalBpm ?? 120);

  useEffect(() => {
    if (!studio) {
      return;
    }

    setBpmState(studio.originalBpm);

    // originalBpm changes whenever a new song is loaded, `setOriginalBpm` also
    // emits a `bpm` change (it resets active BPM), so we listen to that.
    return studio.bpm.subscribe(() => setBpmState(studio.originalBpm));
  }, [studio]);

  return bpm;
}

export function useVolume(): [number, (v: number) => void] {
  const { studio } = useStudio();
  const volume = useStudioObservable<number>(studio?.volume, 80);

  const setVolume = useCallback(
    (next: number) => {
      studio?.setVolume(next);
    },
    [studio],
  );

  return [volume, setVolume];
}

export function useMicController(): MicrophoneController | null {
  return useStudio().micController;
}

export function useMidiController(): MidiController | null {
  return useStudio().midiController;
}

export function useKeyboardController(): KeyboardController | null {
  return useStudio().keyboardController;
}

export function useMicActive(): boolean {
  const mic = useMicController();

  return useStudioObservable<boolean>(mic?.active, false);
}

/**
 * Three-way state surfaced to the toolbar badges:
 *   - `on`          input is currently delivering events to the piano.
 *   - `off`         input is available (permission granted / always available)
 *                   but the user has it disabled, clicking turns it on.
 *   - `unavailable` permission was denied or the API isn't supported,
 *                   clicking still tries to recover (the browser may have
 *                   updated permission since the last probe) but is expected
 *                   to no-op in most cases.
 */
export type DeviceToggleState = "on" | "off" | "unavailable";

interface DeviceToggle {
  state: DeviceToggleState;
  pending: boolean;
  toggle: () => Promise<void> | void;
}

interface MicToggle extends DeviceToggle {
  /**
   * Message du dernier échec, `null` tant que rien n'a échoué. Sans lui, un
   * micro impossible à ouvrir (origine non sécurisée, permission refusée,
   * périphérique occupé) se traduisait par un interrupteur qui revient tout
   * seul en position basse, sans un mot : l'erreur ne partait qu'en console,
   * hors de portée sur une tablette.
   */
  error: string | null;
}

/**
 * Wraps the microphone on/off flow (including the `getUserMedia` permission
 * prompt) so the toolbar badge and the settings overlay share one
 * implementation. `pending` is true while the browser permission prompt is
 * showing, callers should debounce double-clicks against it.
 */
export function useMicToggle(): MicToggle {
  const mic = useMicController();
  const active = useMicActive();
  const permission = useMicPermission();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState<MicUnsupportedReason | null>(null);

  // Sondé après montage seulement : ni `window.isSecureContext` ni
  // `navigator.mediaDevices` n'existent au rendu serveur.
  useEffect(() => {
    setUnsupported(probeMicCapture());
  }, []);

  // `permission === "unsupported"` ne dit rien de la capture elle-même : WebKit
  // ne connaît pas le nom `microphone` dans l'API Permissions alors que
  // `getUserMedia` y fonctionne. Seule l'absence de l'API de capture, ou un
  // refus explicite, rend le micro indisponible.
  const state: DeviceToggleState = active
    ? "on"
    : unsupported !== null || permission === "denied"
      ? "unavailable"
      : "off";

  const toggle = useCallback(async () => {
    if (!mic || pending) {
      return;
    }

    if (active) {
      mic.detach();
      setError(null);

      return;
    }

    const blocked = probeMicCapture();
    setUnsupported(blocked);

    if (blocked !== null) {
      setError(describeMicUnsupported(blocked));

      return;
    }

    setPending(true);
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      mic.attach(stream);
    } catch (err) {
      console.warn("Microphone access denied or unavailable:", err);
      setError(describeMicError(err));
    } finally {
      setPending(false);
    }
  }, [mic, active, pending]);

  return { state, pending, toggle, error };
}

export function useKeyboardActive(): boolean {
  const keyboard = useKeyboardController();

  return useStudioObservable<boolean>(keyboard?.active, false);
}

export function useKeyboardEnabled(): [boolean, (v: boolean) => void] {
  const keyboard = useKeyboardController();
  const enabled = useStudioObservable<boolean>(keyboard?.enabled, true);

  const setEnabled = useCallback(
    (next: boolean) => {
      keyboard?.setEnabled(next);
    },
    [keyboard],
  );

  return [enabled, setEnabled];
}

export function useMicSensitivity(): [number, (value: number) => void] {
  const mic = useMicController();
  const sensitivity = useStudioObservable<number>(mic?.sensitivity, 50);

  const setSensitivity = useCallback(
    (next: number) => {
      mic?.setSensitivity(next);
    },
    [mic],
  );

  return [sensitivity, setSensitivity];
}

export function useMetronomeEnabled(): [boolean, (v: boolean) => void] {
  const { studio } = useStudio();
  const enabled = useStudioObservable<boolean>(studio?.metronomeEnabled, false);

  const setEnabled = useCallback(
    (next: boolean) => {
      // Allumer compte, éteindre non : la propriété dit que le métronome a
      // servi pendant la session, pas dans quel état il a été laissé.
      if (next) {
        recordMetronomeUsed();
      }

      studio?.setMetronomeEnabled(next);
    },
    [studio],
  );

  return [enabled, setEnabled];
}

export function useHighlightActiveBeat(): [boolean, (v: boolean) => void] {
  const { studio } = useStudio();
  const enabled = useStudioObservable<boolean>(studio?.highlightActiveBeat, false);

  const setEnabled = useCallback(
    (next: boolean) => {
      studio?.setHighlightActiveBeat(next);
    },
    [studio],
  );

  return [enabled, setEnabled];
}

export function useNoteEffectStyle(): [NoteEffectStyle, (style: NoteEffectStyle) => void] {
  const { studio } = useStudio();
  const style = useStudioObservable<NoteEffectStyle>(
    studio?.noteEffectStyle,
    DEFAULT_NOTE_EFFECT_STYLE,
  );

  const setStyle = useCallback(
    (next: NoteEffectStyle) => {
      studio?.setNoteEffectStyle(next);
    },
    [studio],
  );

  return [style, setStyle];
}

export function useMuted(): [boolean, (v: boolean) => void] {
  const { studio } = useStudio();
  const muted = useStudioObservable<boolean>(studio?.muted, false);

  const setMuted = useCallback(
    (next: boolean) => {
      studio?.setMuted(next);
    },
    [studio],
  );

  return [muted, setMuted];
}

/**
 * Permission state for a `navigator.permissions` query, `"unsupported"` covers
 * browsers (notably Safari prior to recent versions) that don't expose the
 * `microphone` / `midi` permission name.
 */
export type DevicePermission = "granted" | "prompt" | "denied" | "unsupported";

interface MidiInputsState {
  devices: readonly MidiDeviceInfo[];
  permission: DevicePermission;
  activeDeviceId: string | null;
  setActiveDevice: (id: string | null) => void;
  setEnabled: (id: string, enabled: boolean) => void;
  /** Triggers `requestMIDIAccess` (prompts the user the first time). */
  requestAccess: () => void;
  /** Re-walks the live MIDIAccess input list. No-op when access hasn't been granted yet. */
  rescan: () => void;
}

/**
 * Rapporte qu'un clavier MIDI est branché, une fois par session.
 *
 * Le geste qui sépare un curieux d'un pianiste ne laissait aucune trace dans
 * une application de piano, alors que la liste est en mémoire depuis toujours.
 * Un branchement est ponctuel — contrairement aux notes qu'il produit ensuite,
 * qui sont agrégées ailleurs — d'où un événement et non un état republié à
 * chaque `onChange` : le rescan, le basculement d'un interrupteur et une
 * reconnexion passent tous par ici.
 *
 * Une fois par **session**, et non par périphérique, parce que WebMIDI expose
 * des ports et non des instruments : un seul piano numérique en publie
 * couramment deux, et les bus logiciels (IAC Driver sur macOS, loopMIDI sur
 * Windows) y figurent au même titre qu'un vrai clavier. Une clé par port
 * faisait donc 2 à 6 événements identiques pour un seul instrument branché,
 * tous porteurs du même `deviceCount`, et mettait des bus dans la courbe. Le
 * nombre de ports part en propriété, où il se lit et se moyenne, plutôt qu'en
 * multiplicateur de lignes facturées.
 *
 * Rien du matériel ne sort d'ici : ce qui part est un nombre, jamais un nom ni
 * un identifiant de périphérique.
 */
function reportMidiDevices(devices: readonly MidiDeviceInfo[]): void {
  const connected = devices.filter((device) => device.connected);

  if (connected.length === 0) {
    return;
  }

  trackOnce("midi_device_connected", {
    name: "midi_device_connected",
    properties: { deviceCount: connected.length },
  });
}

/**
 * Subscribes to the WebMIDI singleton owned by the MIDI controller. Devices,
 * the active id, and per-device enabled flags are observed via the input's
 * `onChange` listener so hot-plug events propagate automatically.
 */
export function useMidiInputs(): MidiInputsState {
  const midi = useMidiController();
  const [devices, setDevices] = useState<readonly MidiDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceIdState] = useState<string | null>(null);
  const [permission, setPermission] = useState<DevicePermission>("unsupported");

  // Permission probe, separate from the input itself because we want to
  // surface the badge ("Autoriser") even before `requestMIDIAccess` is called.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) {
      setPermission("unsupported");

      return;
    }

    let cancelled = false;
    let status: PermissionStatus | null = null;
    const handleChange = () => {
      if (cancelled || !status) {
        return;
      }

      setPermission(status.state as DevicePermission);
    };

    navigator.permissions
      // `midi` is not yet in the standard PermissionName union, narrow cast scoped here.
      .query({ name: "midi" as PermissionName })
      .then((s) => {
        if (cancelled) {
          return;
        }

        status = s;
        setPermission(s.state as DevicePermission);
        s.addEventListener("change", handleChange);
      })
      .catch(() => {
        if (!cancelled) {
          setPermission("unsupported");
        }
      });

    return () => {
      cancelled = true;

      if (status) {
        status.removeEventListener("change", handleChange);
      }
    };
  }, []);

  // Subscribe to device-list changes published by the MIDI singleton.
  useEffect(() => {
    if (!midi) {
      return;
    }

    const sync = () => {
      const input = midi.getInput();

      if (!input) {
        setDevices([]);
        setActiveDeviceIdState(null);

        return;
      }

      const list = input.getDevices();

      setDevices(list);
      setActiveDeviceIdState(input.getActiveDeviceId());
      reportMidiDevices(list);
    };

    sync();

    const input = midi.getInput();

    if (!input) {
      return;
    }

    return input.onChange(sync);
  }, [midi, permission]);

  const setActiveDevice = useCallback(
    (id: string | null) => {
      midi?.getInput()?.setActiveDevice(id);
    },
    [midi],
  );

  const setEnabled = useCallback(
    (id: string, enabled: boolean) => {
      midi?.getInput()?.setEnabled(id, enabled);
    },
    [midi],
  );

  const requestAccess = useCallback(() => {
    midi?.attach();
  }, [midi]);

  const rescan = useCallback(() => {
    midi?.getInput()?.rescan();
  }, [midi]);

  return useMemo(
    () => ({
      devices,
      permission,
      activeDeviceId,
      setActiveDevice,
      setEnabled,
      requestAccess,
      rescan,
    }),
    [devices, permission, activeDeviceId, setActiveDevice, setEnabled, requestAccess, rescan],
  );
}

/**
 * Wraps MIDI on/off so the toolbar badge offers the same access flow as
 * the settings overlay. `unavailable` covers the pre-grant / denied /
 * unsupported cases, clicking from there triggers `requestAccess` which is
 * the only way to surface the browser prompt.
 *
 * `on` ↔ `off` toggles enable/disable on connected devices: turning off
 * disables every currently-enabled device, turning on re-enables the first
 * connected one (the controller defaults new devices to `enabled` on first
 * sight, so this is a no-op the very first time access is granted).
 */
export function useMidiToggle(): DeviceToggle {
  const {
    devices,
    permission,
    setEnabled,
    requestAccess,
  } = useMidiInputs();

  const active = devices.some((d) => d.enabled && d.connected);
  const state: DeviceToggleState = active
    ? "on"
    : permission === "granted"
      ? "off"
      : "unavailable";

  const toggle = useCallback(() => {
    if (active) {
      for (const d of devices) {
        if (d.enabled) {
          setEnabled(d.id, false);
        }
      }

      return;
    }

    if (permission !== "granted") {
      requestAccess();

      return;
    }

    const target = devices.find((d) => d.connected) ?? devices[0];

    if (target) {
      setEnabled(target.id, true);
    }
  }, [active, devices, permission, setEnabled, requestAccess]);

  return { state, pending: false, toggle };
}

/** Subscribes to the browser's `microphone` permission state, used for the badge in settings. */
export function useMicPermission(): DevicePermission {
  const [permission, setPermission] = useState<DevicePermission>("unsupported");

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) {
      setPermission("unsupported");

      return;
    }

    let cancelled = false;
    let status: PermissionStatus | null = null;
    const handleChange = () => {
      if (cancelled || !status) {
        return;
      }

      setPermission(status.state as DevicePermission);
    };

    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((s) => {
        if (cancelled) {
          return;
        }

        status = s;
        setPermission(s.state as DevicePermission);
        s.addEventListener("change", handleChange);
      })
      .catch(() => {
        if (!cancelled) {
          setPermission("unsupported");
        }
      });

    return () => {
      cancelled = true;

      if (status) {
        status.removeEventListener("change", handleChange);
      }
    };
  }, []);

  return permission;
}

export function useTonality(): [Tonality | null, (t: Tonality | null) => void] {
  const { studio } = useStudio();
  const tonality = useStudioObservable<Tonality | null>(studio?.tonality, null);

  const setTonality = useCallback(
    (next: Tonality | null) => {
      studio?.setTonality(next);
    },
    [studio],
  );

  return [tonality, setTonality];
}

const DEFAULT_SIGNATURE_FALLBACK: Signature = { num: 4, den: 4 };

export function useTimeSignature(): [Signature, (s: Signature) => void] {
  const { studio } = useStudio();
  const signature = useStudioObservable<Signature>(
    studio?.continuousMetronome.signature,
    DEFAULT_SIGNATURE_FALLBACK,
  );

  // Par le studio, et non par le métronome : en séance, le chiffrage part au
  // service (voir `Studio.setSignature`).
  const setSignature = useCallback(
    (next: Signature) => {
      studio?.setSignature(next);
    },
    [studio],
  );

  return [signature, setSignature];
}

export function useMetronomeDivision(): [DivisionId, (id: DivisionId) => void] {
  const { studio } = useStudio();
  const divisionId = useStudioObservable<DivisionId>(
    studio?.continuousMetronome.division,
    "quarter",
  );

  const setDivision = useCallback(
    (next: DivisionId) => {
      studio?.continuousMetronome.setDivision(next);
    },
    [studio],
  );

  return [divisionId, setDivision];
}

export function useMetronomeAccent(): [boolean, (a: boolean) => void] {
  const { studio } = useStudio();
  const accent = useStudioObservable<boolean>(
    studio?.continuousMetronome.accent,
    true,
  );

  const setAccent = useCallback(
    (next: boolean) => {
      studio?.continuousMetronome.setAccent(next);
    },
    [studio],
  );

  return [accent, setAccent];
}

export function useMetronomeVolume(): [number, (v: number) => void] {
  const { studio } = useStudio();
  const volume = useStudioObservable<number>(studio?.metronome.volume, 80);

  const setVolume = useCallback(
    (next: number) => {
      studio?.metronome.setVolume(next);
    },
    [studio],
  );

  return [volume, setVolume];
}

/**
 * Reflects whether playback is currently progressing, true while the
 * count-in is running (so a tap on Pause cancels it) and while the local
 * transport is unpaused. Subscribes to both sources so the UI stays in sync
 * regardless of who toggles playback (toolbar, journey runtime, learn-piece
 * page, end-of-track auto-pause, …).
 */
export function usePlaybackState(): boolean {
  const { studio } = useStudio();

  /*
    Faux au premier rendu, toujours — et non l'état réel du moteur.

    L'initialiseur paresseux lisait `studio.getLocalTransport()` pendant le
    rendu. Côté serveur il n'y a pas de moteur, donc « à l'arrêt » ; côté
    client, quand ce sous-arbre s'hydrate, le moteur est déjà là et son
    transport déjà attaché — les deux rendus ne disaient donc pas la même
    chose, et React jetait tout le Studio pour le refaire. L'écart est resté
    invisible tant qu'aucune route de `(main)` ne se prérendait vraiment :
    celles qui portaient le Studio derrière elles sortaient toutes du prérendu
    pour d'autres raisons. `/recitals` a cessé d'en sortir le 2026-09-19, et
    l'écart est apparu là.

    L'effet ci-dessous appelle déjà `setPlaying(compute())` à son montage : la
    vraie valeur arrive donc au premier battement qui suit l'hydratation.
    L'initialiseur ne faisait que la donner une frame plus tôt, au prix de la
    seule chose qui pouvait contredire le serveur.
  */
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!studio) {
      return;
    }

    const transport = studio.getLocalTransport();
    const compute = () => studio.countIn.active || !transport.isDetached;

    setPlaying(compute());

    const offTransport = transport.on(() => {
      setPlaying(compute());
    });

    const offCountIn = studio.countIn.on(() => {
      setPlaying(compute());
    });

    return () => {
      offTransport();
      offCountIn();
    };
  }, [studio]);

  return playing;
}
